import * as SelectPrimitive from '@radix-ui/react-select';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type FormEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { isApiError } from '../api/http';
import { apiErrorText, useI18n } from '../i18n';
import { Icon, type IconName } from './icon';

// Material's outlined text field, select, checkbox, radio and switch (TF-91). The forms of the app
// are made of these: the label floats onto the outline, the hint or the error stands under the
// field, the browser's checks are shown in the app's words where the field is (on leaving it and on
// sending the form), and what only the server can know is shown at the field the server names. A
// field's `name` is the API's name for it: that is how an error finds its field.

// -------------------------------------------------------------------------------- errors

/** An error a form finds itself (a postal code that does not fit its country), at a field or not. */
export class FieldError extends Error {
  constructor(
    message: string,
    readonly field?: string,
  ) {
    super(message);
  }
}

/** The field an error is about, and its message; null when it is about the whole form. */
export function fieldErrorOf(error: unknown): { field: string; message: string } | null {
  // An answer the app has words for is said in the app's language (TF-112), at its field as anywhere.
  if (isApiError(error) && error.field) return { field: error.field, message: apiErrorText(error.code, error.details) ?? error.message };
  if (error instanceof FieldError && error.field) return { field: error.field, message: error.message };
  return null;
}

/**
 * The errors of a form by field name, each with the failure it came from: the same message after a
 * new failure (the same mistake made again) shows again.
 */
const FieldErrors = createContext<Record<string, { message: string; cause: unknown }>>({});

/** The message of an error about the whole form. */
function formMessage(error: unknown, fallback: string): string | null {
  if (!error || fieldErrorOf(error)) return null;
  if (typeof error === 'string') return error;
  if (isApiError(error)) return apiErrorText(error.code, error.details) ?? error.message;
  return error instanceof FieldError ? error.message : fallback;
}

/** The first control of a form that is wrong: focused, or its field's own control when it is hidden. */
function focusControl(control: Element | null) {
  if (!(control instanceof HTMLElement)) return;
  const own = control.closest('.tf')?.querySelector<HTMLElement>('.tf-control');
  (control.getAttribute('aria-hidden') === 'true' || control.tabIndex < 0 ? own : control)?.focus();
}

/**
 * A form (TF-91). On sending, the browser's own checks run, but each field shows its message under
 * itself instead of the browser's bubble, the first field that is wrong gets the focus, and nothing
 * is sent. `error` is the last failure, from the server or the form's own checks (a string, an
 * ApiError or a FieldError): one about a field shows at that field and focuses it, any other at the
 * top of the form. A form with required fields says once what the asterisk means.
 */
export function Form({
  onSubmit,
  error,
  className,
  id,
  children,
}: {
  onSubmit: () => void;
  error?: unknown;
  className?: string;
  id?: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLFormElement>(null);
  const fieldError = fieldErrorOf(error);
  const [hasRequired, setHasRequired] = useState(false);
  useLayoutEffect(() => {
    setHasRequired(Boolean(ref.current?.querySelector('[required], [aria-required="true"]')));
  });
  useEffect(() => {
    if (!fieldError || !ref.current) return;
    focusControl(ref.current.querySelector(`[name="${CSS.escape(fieldError.field)}"]`));
  }, [fieldError?.field, fieldError?.message]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // A dialog's form is drawn in a portal, but React bubbles its submit up the component tree: an
    // outer form (the form builder around its question dialog, TF-328) must not take it as its own.
    event.stopPropagation();
    const form = event.currentTarget;
    // Fires "invalid" on every field that is wrong: each shows its own message.
    if (!form.checkValidity()) {
      focusControl(form.querySelector(':invalid:not(fieldset)'));
      return;
    }
    onSubmit();
  };
  const message = formMessage(error, t('common.somethingWrong'));
  const byField = useMemo(() => (fieldError ? { [fieldError.field]: { message: fieldError.message, cause: error } } : {}), [error]);
  return (
    <FieldErrors.Provider value={byField}>
      <form ref={ref} id={id} className={className} noValidate onSubmit={submit}>
        {message && (
          <p role="alert" className="form-error">
            {message}
          </p>
        )}
        {hasRequired && <p className="form-note">{t('field.requiredNote')}</p>}
        {children}
      </form>
    </FieldErrors.Provider>
  );
}

/** The form's error about a field by name, with the failure it came from; for a field drawn elsewhere. */
export function useFieldError(name: string): { message: string; cause: unknown } | undefined {
  return useContext(FieldErrors)[name];
}

/** The error about a group of controls that is not one field (a set of chips), under the group. */
export function FieldGroupError({ name }: { name: string }) {
  const message = useContext(FieldErrors)[name]?.message;
  return message ? (
    <span role="alert" className="field-error">
      {message}
    </span>
  ) : null;
}

type Control = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
type Translate = ReturnType<typeof useI18n>['t'];

/** What the browser found wrong with a field, in the app's words. */
function validityMessage(control: Control, t: Translate): string {
  const validity = control.validity;
  const input = control as HTMLInputElement;
  if (validity.valueMissing) return t('field.required');
  if (validity.typeMismatch) return input.type === 'email' ? t('field.email') : input.type === 'url' ? t('field.url') : t('field.format');
  if (validity.tooLong) return t('field.tooLong', { max: input.maxLength });
  if (validity.tooShort) return t('field.tooShort', { min: input.minLength });
  if (validity.rangeUnderflow) return t('field.min', { min: input.min });
  if (validity.rangeOverflow) return t('field.max', { max: input.max });
  if (validity.badInput || validity.stepMismatch) return t('field.number');
  if (validity.patternMismatch) return input.title || t('field.format');
  return control.validationMessage;
}

/**
 * The message a field shows: the one it is given, else the server's for its name (until it is
 * changed), else what the browser found (on leaving it or sending the form; while wrong, checked
 * again as it is typed).
 */
export function useFieldMessage(name: string | undefined, given: string | null | undefined) {
  const { t } = useI18n();
  const server = useContext(FieldErrors)[name ?? ''];
  const [found, setFound] = useState<string | null>(null);
  const [serverShown, setServerShown] = useState<string | null>(null);
  useEffect(() => setServerShown(server?.message ?? null), [server?.cause]);
  return {
    message: given ?? serverShown ?? found,
    /** Caught at the field's wrapper: the invalid event of any control inside, the hidden one of a menu too. */
    onInvalidCapture: (event: FormEvent<HTMLElement>) => {
      event.preventDefault();
      setFound(validityMessage(event.target as Control, t));
    },
    onBlur: (event: FocusEvent<Control>) => {
      event.currentTarget.checkValidity();
    },
    onChanged: (control: Control | null) => {
      setServerShown(null);
      if (found !== null && (!control || control.checkValidity())) setFound(null);
    },
  };
}

// --------------------------------------------------------------------------------- chrome

export interface FieldChrome {
  label: string;
  /** Supporting text under the field; the error takes its place while there is one. */
  hint?: ReactNode;
  error?: string | null;
  leadingIcon?: IconName;
  /** An icon button at the end (clear, show the password); an error puts its icon there instead. */
  trailing?: ReactNode;
  /** The trailing button stays beside an error's icon (a date field's calendar, TF-92). */
  keepTrailing?: boolean;
  prefix?: string;
  suffix?: string;
  /** Inside the box, before the control: the chosen tags of a tag filter (TF-62); the label then stands on the outline. */
  before?: ReactNode;
  /** 48 high instead of 56: filter bars and editing inside a table. */
  dense?: boolean;
  /** The label is for screen readers only (a field whose place says what it is). */
  hideLabel?: boolean;
  className?: string;
}

/**
 * The outline with its floating label around a control, and the hint, error or counter under it.
 * Exported for the fields made of more than one control (the time field, TF-92).
 */
export function Chrome({
  id,
  chrome,
  required,
  disabled,
  message,
  count,
  up,
  kind,
  onInvalidCapture,
  children,
}: {
  id: string;
  chrome: FieldChrome;
  required?: boolean;
  disabled?: boolean;
  message: string | null;
  count?: string;
  up: boolean;
  kind: 'input' | 'textarea' | 'select';
  onInvalidCapture: (event: FormEvent<HTMLElement>) => void;
  children: ReactNode;
}) {
  const classes = ['tf', `tf-${kind}`];
  if (chrome.dense) classes.push('tf-dense');
  if (message) classes.push('tf-invalid');
  if (up || chrome.prefix || chrome.suffix || chrome.hideLabel || chrome.before) classes.push('tf-up');
  if (chrome.leadingIcon) classes.push('tf-lead');
  if (disabled) classes.push('tf-disabled');
  if (chrome.className) classes.push(chrome.className);
  const below = message ?? chrome.hint;
  // The asterisk stands beside the label, not in it: the field's name stays its label alone.
  const mark = required && (
    <span className="tf-required" aria-hidden="true">
      {' '}
      *
    </span>
  );
  return (
    <div className={classes.join(' ')} onInvalidCapture={onInvalidCapture}>
      <div className="tf-box">
        {chrome.leadingIcon && <Icon name={chrome.leadingIcon} className="tf-lead-icon" />}
        {chrome.prefix && <span className="tf-affix">{chrome.prefix}</span>}
        {chrome.before}
        {children}
        {chrome.suffix && <span className="tf-affix">{chrome.suffix}</span>}
        {message && <Icon name="error" className="tf-error-icon" />}
        {(!message || chrome.keepTrailing) && chrome.trailing}
        {/* Under the box, in the room the field leaves below it: showing it while typing moves nothing.
            Always in the page and shown by CSS while the field has focus (TF-294): adding and removing
            it on focus and blur changed the page between two fields, and a dialog's focus trap then
            took the focus back to the dialog, so Tab never reached the next field. */}
        {count && (
          <span className="tf-count" aria-hidden="true">
            {count}
          </span>
        )}
        {/* A hidden label is the control's aria-label instead (the components pass it). */}
        {!chrome.hideLabel && (
          <span className="tf-label">
            <label htmlFor={id}>{chrome.label}</label>
            {mark}
          </span>
        )}
        <fieldset className="tf-outline" aria-hidden="true">
          {!chrome.hideLabel && (
            <legend>
              <span>
                {chrome.label}
                {mark}
              </span>
            </legend>
          )}
        </fieldset>
      </div>
      {below && (
        <div className="tf-below">
          <span id={`${id}-below`} className="tf-support" role={message ? 'alert' : undefined}>
            {below}
          </span>
        </div>
      )}
    </div>
  );
}

/** Kinds of input whose box always shows something, so their label always stands on the outline. */
const ALWAYS_UP = ['month', 'week', 'color'];

// ----------------------------------------------------------------------------- text field

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'prefix' | 'size'> & {
  value: string | number;
  onChange: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * What is wrong with a value the browser cannot judge (a typed date that is no day), in words, or ""
   * when nothing is: shown and refused on sending like the browser's own checks.
   */
  validate?: (value: string) => string;
};

/** Material's outlined text field: its label floats onto the outline; a hint or an error under it. */
export const TextField = forwardRef(function TextField(
  { label, hint, error, leadingIcon, trailing, keepTrailing, prefix, suffix, before, dense, hideLabel, className, id: givenId, value, onChange, onBlur, onFocus, validate, ...input }: FieldChrome & InputProps,
  ref: Ref<HTMLInputElement>,
) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const field = useFieldMessage(input.name, error);
  const text = String(value ?? '');
  const max = input.maxLength;
  // The field's own check, kept on the control as its custom validity, so the form's checks see it.
  const control = useRef<HTMLInputElement | null>(null);
  const problem = validate?.(text) ?? '';
  useLayoutEffect(() => {
    control.current?.setCustomValidity(problem);
  }, [problem]);
  const setRef = (element: HTMLInputElement | null) => {
    control.current = element;
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
  };
  return (
    <Chrome
      id={id}
      chrome={{ label, hint, leadingIcon, trailing, keepTrailing, prefix, suffix, before, dense, hideLabel, className }}
      required={input.required}
      disabled={input.disabled}
      message={field.message}
      count={max && max > 0 ? `${text.length} / ${max}` : undefined}
      up={text !== '' || ALWAYS_UP.includes(input.type ?? '') || Boolean(input.placeholder)}
      kind="input"
      onInvalidCapture={field.onInvalidCapture}
    >
      <input
        {...input}
        ref={setRef}
        id={id}
        value={value}
        // Where a dialog puts the focus when it opens (TF-294): see focusOnOpen in ui/index.tsx.
        data-autofocus={input.autoFocus ? '' : undefined}
        className="tf-control"
        aria-label={hideLabel ? label : input['aria-label']}
        aria-invalid={field.message ? true : undefined}
        aria-describedby={field.message || hint ? `${id}-below` : undefined}
        onFocus={onFocus}
        onBlur={(event) => {
          field.onBlur(event);
          onBlur?.(event);
        }}
        onChange={(event) => {
          if (validate) event.currentTarget.setCustomValidity(validate(event.currentTarget.value));
          field.onChanged(event.currentTarget);
          onChange(event.currentTarget.value, event);
        }}
      />
    </Chrome>
  );
});

type AreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> & { value: string; onChange: (value: string) => void };

/** The multi-line text field, for notes: it grows with its text up to a limit. */
export function TextArea({ label, hint, error, dense, hideLabel, className, id: givenId, value, onChange, onBlur, onFocus, rows = 3, ...area }: FieldChrome & AreaProps) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const field = useFieldMessage(area.name, error);
  const max = area.maxLength;
  return (
    <Chrome
      id={id}
      chrome={{ label, hint, dense, hideLabel, className }}
      required={area.required}
      disabled={area.disabled}
      message={field.message}
      count={max && max > 0 ? `${value.length} / ${max}` : undefined}
      up={value !== '' || Boolean(area.placeholder)}
      kind="textarea"
      onInvalidCapture={field.onInvalidCapture}
    >
      <textarea
        {...area}
        id={id}
        rows={rows}
        value={value}
        data-autofocus={area.autoFocus ? '' : undefined}
        className="tf-control"
        aria-label={hideLabel ? label : area['aria-label']}
        aria-invalid={field.message ? true : undefined}
        aria-describedby={field.message || hint ? `${id}-below` : undefined}
        onFocus={onFocus}
        onBlur={(event) => {
          field.onBlur(event);
          onBlur?.(event);
        }}
        onChange={(event) => {
          field.onChanged(event.currentTarget);
          onChange(event.currentTarget.value);
        }}
      />
    </Chrome>
  );
}

// ------------------------------------------------------------------------------------ select

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

/** A list longer than this stays the phone's own picker (TF-91, answer 2). */
export const LONG_LIST = 15;

/** A phone: a finger and a compact window. Read once per field; a phone does not become a computer. */
const isPhone = () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse) and (max-width: 599px)').matches;

/** Radix keeps no empty value: "none" travels as this. */
const NONE = '__none__';

/**
 * A choice from a list: Material's menu under an outlined field (Radix Select, with type-ahead, the
 * keyboard, and the chosen item marked). On a phone a long list is the system's own picker inside
 * the same field (country, language, anything over LONG_LIST); `native` asks for it everywhere. An
 * empty value means nothing is chosen; with `placeholder`, "nothing" is also on the list.
 */
export function SelectField({
  label,
  hint,
  error,
  dense,
  hideLabel,
  className,
  id: givenId,
  name,
  value,
  onChange,
  options,
  groups,
  placeholder,
  required,
  disabled,
  native,
  autoFocus,
}: FieldChrome & {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  groups?: SelectGroup[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  native?: boolean;
  autoFocus?: boolean;
}) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const field = useFieldMessage(name, error);
  const [phone] = useState(isPhone);
  const [open, setOpen] = useState(false);
  const all = [...(options ?? []), ...(groups ?? []).flatMap((group) => group.options)];
  const chosen = all.find((option) => option.value === value);
  const chrome = { label, hint, dense, hideLabel, className };
  // "Nothing" is a choice only where the field may stay empty; a required one shows the placeholder
  // until something is chosen, and the form's check stops it while it is empty.
  const noneChoice = placeholder !== undefined && !required;
  const described = field.message || hint ? `${id}-below` : undefined;

  if (native || (phone && all.length > LONG_LIST)) {
    const option = (item: SelectOption) => (
      <option key={item.value} value={item.value} disabled={item.disabled}>
        {item.label}
      </option>
    );
    return (
      <Chrome id={id} chrome={chrome} required={required} disabled={disabled} message={field.message} up kind="select" onInvalidCapture={field.onInvalidCapture}>
        <select
          id={id}
          name={name}
          className="tf-control"
          value={value}
          required={required}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={hideLabel ? label : undefined}
          aria-invalid={field.message ? true : undefined}
          aria-describedby={described}
          onBlur={field.onBlur}
          onChange={(event) => {
            field.onChanged(event.currentTarget);
            onChange(event.currentTarget.value);
          }}
        >
          {(placeholder !== undefined || !chosen) && (
            <option value="" disabled={required && placeholder !== undefined}>
              {placeholder ?? ''}
            </option>
          )}
          {options?.map(option)}
          {groups?.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map(option)}
            </optgroup>
          ))}
        </select>
        <Icon name="arrow_drop_down" className="tf-arrow" />
      </Chrome>
    );
  }

  const item = (option: SelectOption) => (
    // data-value: the value, where tests and tools can find it (the menu shows only the label).
    <SelectPrimitive.Item key={option.value || NONE} value={option.value || NONE} data-value={option.value || NONE} disabled={option.disabled} className="menu-item select-item">
      <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="select-check">
        <Icon name="check" size={20} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
  return (
    <Chrome
      id={id}
      chrome={chrome}
      required={required}
      disabled={disabled}
      message={field.message}
      up={Boolean(chosen) || placeholder !== undefined}
      kind="select"
      onInvalidCapture={field.onInvalidCapture}
    >
      <SelectPrimitive.Root
        value={value === '' ? (noneChoice ? NONE : '') : value}
        onValueChange={(next) => {
          field.onChanged(null);
          onChange(next === NONE ? '' : next);
        }}
        name={name}
        required={required}
        disabled={disabled}
        onOpenChange={setOpen}
      >
        <SelectPrimitive.Trigger
          id={id}
          className="tf-control tf-trigger"
          data-value={value}
          autoFocus={autoFocus}
          aria-label={hideLabel ? label : undefined}
          aria-invalid={field.message ? true : undefined}
          aria-describedby={described}
        >
          <SelectPrimitive.Value placeholder={placeholder ?? ''} />
        </SelectPrimitive.Trigger>
        <Icon name="arrow_drop_down" className="tf-arrow" />
        <SelectPrimitive.Portal>
          {/* The menu is named after its field while it is open: a screen reader says which list it is
              (TF-455). Closing, it stays a moment for its animation, unnamed, so the field's label
              names one thing only. */}
          <SelectPrimitive.Content className="menu select-menu" position="popper" sideOffset={4} collisionPadding={8} aria-label={open ? label : undefined}>
            <SelectPrimitive.Viewport className="select-viewport">
              {noneChoice && item({ value: '', label: placeholder ?? '' })}
              {options?.map(item)}
              {groups?.map((group) => (
                <SelectPrimitive.Group key={group.label}>
                  <SelectPrimitive.Label className="menu-label">{group.label}</SelectPrimitive.Label>
                  {group.options.map(item)}
                </SelectPrimitive.Group>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </Chrome>
  );
}

// ------------------------------------------------------------------- checkbox, radio, switch

type ChoiceProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'children'> & {
  label: ReactNode;
  onChange: (checked: boolean) => void;
  hint?: ReactNode;
  /** The label is for screen readers only (a tick box in a table row). */
  hideLabel?: boolean;
};

function ChoiceText({ label, hint, hideLabel }: { label: ReactNode; hint?: ReactNode; hideLabel?: boolean }) {
  // A hidden label given as text is the input's aria-label (hiddenName): nothing to show here.
  if (hideLabel && typeof label === 'string') return null;
  return (
    <span className={hideLabel ? 'visually-hidden' : 'choice-text'}>
      {label}
      {hint && <span className="choice-hint">{hint}</span>}
    </span>
  );
}

/** The aria-label of an input whose label is hidden and given as text. */
const hiddenName = (label: ReactNode, hideLabel?: boolean) => (hideLabel && typeof label === 'string' ? label : undefined);

/**
 * Material's checkbox: 18 across, a 40 state layer, the label beside it takes the click too, and a
 * finger gets 48. `indeterminate` is "some but not all" (select all over a partly ticked list).
 */
export function Checkbox({ label, hint, hideLabel, onChange, indeterminate = false, className, ...input }: ChoiceProps & { indeterminate?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className={className ? `choice ${className}` : 'choice'}>
      <span className="choice-target">
        <input {...input} ref={ref} type="checkbox" className="checkbox" aria-label={hiddenName(label, hideLabel) ?? input['aria-label']} onChange={(event) => onChange(event.currentTarget.checked)} />
      </span>
      <ChoiceText label={label} hint={hint} hideLabel={hideLabel} />
    </label>
  );
}

/** Material's radio button, with the checkbox's sizes. */
export function Radio({ label, hint, hideLabel, onChange, className, ...input }: ChoiceProps) {
  return (
    <label className={className ? `choice ${className}` : 'choice'}>
      <span className="choice-target">
        <input {...input} type="radio" className="radio" aria-label={hiddenName(label, hideLabel) ?? input['aria-label']} onChange={(event) => onChange(event.currentTarget.checked)} />
      </span>
      <ChoiceText label={label} hint={hint} hideLabel={hideLabel} />
    </label>
  );
}

/** Material's switch: 52 by 32; the handle grows and carries a check when it is on. */
export function Switch({ label, hint, hideLabel, onChange, className, ...input }: ChoiceProps) {
  return (
    <label className={className ? `choice choice-switch ${className}` : 'choice choice-switch'}>
      <ChoiceText label={label} hint={hint} hideLabel={hideLabel} />
      <input {...input} type="checkbox" role="switch" className="switch" aria-label={hiddenName(label, hideLabel) ?? input['aria-label']} onChange={(event) => onChange(event.currentTarget.checked)} />
    </label>
  );
}
